import {
  FirebaseStorageTypes,
  getDownloadURL,
  getMetadata,
  getStorage,
  listAll,
  putFile,
  ref
} from '@react-native-firebase/storage';
import {
  deleteObject,
  FirebaseStorage,
  FullMetadata,
  getDownloadURL as getDownloadURLWeb,
  getMetadata as getMetadataWeb,
  listAll as listAllWeb,
  ref as storageRef,
  uploadBytesResumable as uploadBytesResumableWeb
} from 'firebase/storage';
import { Platform } from 'react-native';

import { storage } from '@/firebaseConfig';

export interface FileToUpload {
  uri: string;
  name: string;
  blob?: Blob;
}

export interface UploadResult {
  downloadUrl: string;
  metadata: FirebaseStorageTypes.FullMetadata | FullMetadata;
}

const uploadMultipleToFireStorageMobile = async (
  path: string,
  files: FileToUpload[],
  onProgress?: (index: number, progress: number) => void
): Promise<UploadResult[]> => {
  const folderRef = ref(getStorage(), path);

  // Delete all the files that are not in the FilesToUpload[] and avoid deleting the ones that are already uploaded
  await listAll(folderRef).then(async (listResults) => {
    const existingItems = files.filter((existingFile) => existingFile.uri.includes('firebasestorage'));
    const deletePromises = listResults.items.map(async (item) => {
      if (
        !existingItems.find((existing) => {
          // Extract the path portion after "/o/" and before "?"
          const encodedPath = existing.uri.match(/\/o\/(.+?)\?/i)?.[1];
          if (encodedPath) {
            // Decode the URL-encoded path
            const decodedPath = decodeURIComponent(encodedPath);
            return decodedPath === item.fullPath;
          }
        })
      ) {
        return item.delete();
      }
    });
    await Promise.all(deletePromises); // Ensure all deletions complete before uploading
  });

  // Upload the files directly from their local path on native
  const uploadPromises = files.map((file, index) => {
    const { uri, name } = file;

    if (uri.includes('firebasestorage')) {
      return new Promise<UploadResult>(async (resolve, reject) => {
        try {
          const existingItems = await listAll(folderRef);
          const existingFile = existingItems.items.find((existing) => {
            // Extract the path portion after "/o/" and before "?" to match with firebase storage item name
            const encodedPath = file.uri.match(/\/o\/(.+?)\?/i)?.[1];
            if (encodedPath) {
              // Decode the URL-encoded path
              const decodedPath = decodeURIComponent(encodedPath);
              return decodedPath === existing.fullPath;
            }
          });
          if (existingFile) {
            resolve({
              downloadUrl: await getDownloadURL(existingFile),
              metadata: await getMetadata(existingFile)
            });
          } else {
            reject('File already uploaded not found');
          }
        } catch (e) {
          console.warn(e);
          reject(e);
        }
      });
    }

    const fileRef = ref(getStorage(), `${path}${name}`);
    const uploadTask = putFile(fileRef, uri);

    return new Promise<UploadResult>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress && onProgress(index, progress);
        },
        (error) => {
          console.error(`Error uploading file ${name}:`, error);
          reject(error);
        },
        async () => {
          try {
            if (uploadTask.snapshot) {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                downloadUrl,
                metadata: uploadTask.snapshot.metadata
              });
            }
          } catch (error) {
            console.error(`Error getting download URL for file ${name}:`, error);
            reject(error);
          }
        }
      );
    });
  });

  // Wait for all the file uploads to complete
  return Promise.all(uploadPromises);
};

const uploadMultipleToFireStorageWeb = async (
  path: string,
  files: FileToUpload[],
  onProgress?: (index: number, progress: number) => void
): Promise<UploadResult[]> => {
  const folderRef = storageRef(storage() as FirebaseStorage, path);

  // Delete all the files that are not in the FilesToUpload[] and avoid deleting the ones that are already uploaded
  await listAllWeb(folderRef).then(async (listResults) => {
    const existingItems = files.filter((existingFile) => existingFile.uri.includes('firebasestorage'));
    const deletePromises = listResults.items.map(async (item) => {
      if (
        !existingItems.find((existing) => {
          // Extract the path portion after "/o/" and before "?"
          const encodedPath = existing.uri.match(/\/o\/(.+?)\?/i)?.[1];
          if (encodedPath) {
            // Decode the URL-encoded path
            const decodedPath = decodeURIComponent(encodedPath);
            return decodedPath === item.fullPath;
          }
        })
      ) {
        return deleteObject(item);
      }
    });
    await Promise.all(deletePromises); // Ensure all deletions complete before uploading
  });

  // Upload the files with the fetched blob from the new uri or the one already saved before
  const uploadPromises = files.map((file, index) => {
    const { uri, name, blob } = file;

    if (uri.includes('firebasestorage')) {
      return new Promise<UploadResult>(async (resolve, reject) => {
        try {
          const existingItems = await listAllWeb(folderRef);
          const existingFile = existingItems.items.find((existing) => {
            // Extract the path portion after "/o/" and before "?" to match with firebase storage item name
            const encodedPath = file.uri.match(/\/o\/(.+?)\?/i)?.[1];
            if (encodedPath) {
              // Decode the URL-encoded path
              const decodedPath = decodeURIComponent(encodedPath);
              return decodedPath === existing.fullPath;
            }
          });
          if (existingFile) {
            resolve({
              downloadUrl: await getDownloadURLWeb(existingFile),
              metadata: await getMetadataWeb(existingFile)
            });
          } else {
            reject('File already uploaded not found');
          }
        } catch (e) {
          console.warn(e);
          reject(e);
        }
      });
    }

    const fetchBlob = async () => {
      if (blob) return blob;
      const fetchResponse = await fetch(uri);
      return fetchResponse.blob();
    };

    const uploadPromise = fetchBlob().then((theBlob) => {
      const fileRef = storageRef(storage() as FirebaseStorage, `${path}${name}`);
      const uploadTask = uploadBytesResumableWeb(fileRef, theBlob);

      return new Promise<UploadResult>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress && onProgress(index, progress);
          },
          (error) => {
            console.error(`Error uploading file ${name}:`, error);
            reject(error);
          },
          async () => {
            try {
              if (uploadTask.snapshot) {
                const downloadUrl = await getDownloadURLWeb(uploadTask.snapshot.ref);
                resolve({
                  downloadUrl,
                  metadata: uploadTask.snapshot.metadata
                });
              }
            } catch (error) {
              console.error(`Error getting download URL for file ${name}:`, error);
              reject(error);
            }
          }
        );
      });
    });

    return uploadPromise;
  });

  // Wait for all the file uploads to complete
  return Promise.all(uploadPromises);
};

/**
 * Util function to upload multiple files to firebase storage
 * @param path - Path of storage where to save the files and delete the old ones
 * @param files - Array of file objects with uri and name properties of type FileToUpload[]
 * @param onProgress - Optional callback function with progress value for each file
 * @returns Promise with an array of objects containing downloadUrl and metadata for each file
 */
export const uploadMultipleToFireStorage = (
  path: string,
  files: FileToUpload[],
  onProgress?: (index: number, progress: number) => void
): Promise<UploadResult[]> => {
  if (Platform.OS === 'web') return uploadMultipleToFireStorageWeb(path, files, onProgress);
  return uploadMultipleToFireStorageMobile(path, files, onProgress);
};
